import React, { useEffect, useState } from "react";

/**
 * ButtonReportDefinitionsEditor (with report_type)
 *
 * - GET /button_report_definitions
 * - GET /report_groups
 * - PUT /button_report_definitions/:id
 * - POST /button_report_definitions
 * - DELETE /button_report_definitions/:id
 *
 * Adds `report_type` dropdown (1 = Service, 2 = Supplies).
 * Time fields are minutes in the UI (15..720) and converted to seconds for the backend.
 */

const createMinuteOptions = () => {
  const opts = [];
  for (let m = 15; m <= 720; m += 15) opts.push(m);
  return opts;
};

const minuteOptions = createMinuteOptions();

const minutesLabel = (m) => {
  if (!m || m === 0) return "—";
  if (m % 60 === 0) {
    const hours = m / 60;
    return hours === 1 ? "1 hr" : `${hours} hrs`;
  }
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  if (hours === 0) return `${mins} min`;
  return `${hours} hr${hours > 1 ? "s" : ""} ${mins} min`;
};

// Mapping for report types
const REPORT_TYPES = [
  { value: "1", label: "Novērtējums" },
  { value: "2", label: "Pieprasījums" },
];

export default function ButtonReportDefinitionsEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});

  const [newRow, setNewRow] = useState({
    serial: "",
    button_id: "",
    report_text: "",
    max_age_minutes: 15,
    max_frequency_minutes: 15,
    report_type: "1", // default to Service
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [defsRes, /*groupsRes*/] = await Promise.all([
        fetch(process.env.REACT_APP_BACKEND_URL+"/button_report_definitions"),
        /*fetch(process.env.REACT_APP_BACKEND_URL+"/report_groups"),*/
      ]);

      if (!defsRes.ok) throw new Error(`GET /button_report_definitions ${defsRes.status}`);
      const defs = await defsRes.json();

      const converted = (Array.isArray(defs) ? defs : []).map((r) => {
        const maxAgeMin = r.max_age ? Math.round(r.max_age / 60) : 0;
        const maxFreqMin = r.max_frequency ? Math.round(r.max_frequency / 60) : 0;
        return {
          ...r,
          max_age_minutes: maxAgeMin,
          max_frequency_minutes: maxFreqMin,
          // ensure report_type present as string for select
          report_type: r.report_type != null ? String(r.report_type) : "1",
        };
      });
      setRows(converted);

      /*if (groupsRes.ok) {
        const groupsData = await groupsRes.json();
        setGroups(Array.isArray(groupsData) ? groupsData : []);
      } else {
        setGroups([]);
      }*/
    } catch (err) {
      console.error(err);
      setError("Failed to load definitions (see console)");
      setRows([]);
      /*setGroups([]);*/
    } finally {
      setLoading(false);
    }
  };

  // Start editing a row
  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      report_text: row.report_text || "",
      max_age_minutes: row.max_age_minutes || 15,
      max_frequency_minutes: row.max_frequency_minutes || 15,
      report_type: row.report_type ?? "1",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({});
  };

  const handleFormChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // Save edited row (PUT)
  const saveEdit = async () => {
    if (!editingId) return;
    if (!form.report_text || form.report_text.trim() === "") {
      alert("Please enter report text.");
      return;
    }
    const payload = {
      report_text: form.report_text,
      max_age: Number(form.max_age_minutes) * 60,
      max_frequency: Number(form.max_frequency_minutes) * 60,
      report_type: Number(form.report_type), // ensure numeric
    };
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/button_report_definitions/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`PUT failed ${res.status}`);
      await fetchAll();
      cancelEdit();
    } catch (err) {
      console.error(err);
      alert("Save failed — see console");
    }
  };

  // Delete row
  const deleteRow = async (id) => {
    if (!window.confirm("Delete this definition?")) return;
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+`/button_report_definitions/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`DELETE failed ${res.status}`);
      await fetchAll();
    } catch (err) {
      console.error(err);
      alert("Delete failed — see console");
    }
  };

  // Add new row (POST)
  const addNewRow = async () => {
    if (!newRow.serial || !newRow.button_id) {
      alert("Please provide device serial and button id.");
      return;
    }
    if (!newRow.report_text || newRow.report_text.trim() === "") {
      alert("Please provide report text.");
      return;
    }

    const payload = {
      serial: newRow.serial,
      button_id: Number(newRow.button_id),
      report_text: newRow.report_text,
      max_age: Number(newRow.max_age_minutes) * 60,
      max_frequency: Number(newRow.max_frequency_minutes) * 60,
      report_type: Number(newRow.report_type),
    };

    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL+"/button_report_definitions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`POST failed ${res.status}`);
      setNewRow({
        serial: "",
        button_id: "",
        report_text: "",
        max_age_minutes: 15,
        max_frequency_minutes: 15,

        report_type: "1",
      });
      await fetchAll();
    } catch (err) {
      console.error(err);
      alert("Add failed — see console");
    }
  };

  if (loading) return <div className="p-4">Loading definitions...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Notikumu definīcijas</h2>

      <table className="table-auto border w-full mb-6">
        <thead>
          <tr className="bg-gray-200">
            <th className="border px-2">Pogas ID</th>
            <th className="border px-2">Paziņojuma teksts</th>
            <th className="border px-2">Maks. vecums</th>
            <th className="border px-2">Maks. biežums</th>
            <th className="border px-2">Tips</th>
            <th className="border px-2">Darbības</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="border px-2">{row.serial}#{row.button_id}</td>

              <td className="border px-2">
                {editingId === row.id ? (
                  <input
                    className="border p-1 w-full"
                    value={form.report_text || ""}
                    onChange={(e) => handleFormChange("report_text", e.target.value)}
                  />
                ) : (
                  row.report_text
                )}
              </td>

              <td className="border px-2">
                {editingId === row.id ? (
                  <select
                    className="border p-1"
                    value={form.max_age_minutes}
                    onChange={(e) => handleFormChange("max_age_minutes", Number(e.target.value))}
                  >
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {minutesLabel(m)}
                      </option>
                    ))}
                  </select>
                ) : (
                  minutesLabel(row.max_age_minutes)
                )}
              </td>

              <td className="border px-2">
                {editingId === row.id ? (
                  <select
                    className="border p-1"
                    value={form.max_frequency_minutes}
                    onChange={(e) => handleFormChange("max_frequency_minutes", Number(e.target.value))}
                  >
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>
                        {minutesLabel(m)}
                      </option>
                    ))}
                  </select>
                ) : (
                  minutesLabel(row.max_frequency_minutes)
                )}
              </td>

              {/* REPORT TYPE */}
              <td className="border px-2">
                {editingId === row.id ? (
                  <select
                    className="border p-1"
                    value={form.report_type ?? "1"}
                    onChange={(e) => handleFormChange("report_type", e.target.value)}
                  >
                    {REPORT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  REPORT_TYPES.find((t) => t.value === String(row.report_type))?.label || "—"
                )}
              </td>

              <td className="border px-2">
                {editingId === row.id ? (
                  <>
                    <button
                      className="mr-2 px-2 py-1 bg-green-500 text-white rounded"
                      onClick={saveEdit}
                    >
                      Saglabāt
                    </button>
                    <button className="px-2 py-1 bg-gray-400 text-white rounded" onClick={cancelEdit}>
                      Atcelt
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="mr-2 px-2 py-1 bg-blue-500 text-white rounded"
                      onClick={() => startEdit(row)}
                    >
                      Labot
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
